import {
  FormattedRequestParamsType,
  RequestParamsType,
} from '@redux/crudCreator/types';
import { TFilterRequest } from 'components/rest/Filter/FilterContent';
import { compact, keyBy, omit } from 'lodash';
import { DynamicObjectType } from 'types/commons';
import { getValidData } from 'utils/tools';

export const PRIMARY_KEY = 'id';

export const REQUEST_TYPE = {
  GET_ALL: 'GET_ALL',
  GET_BY_ID: 'GET_BY_ID',
  EDIT: 'EDIT',
  CREATE: 'CREATE',
  DELETE: 'DELETE',
};

const getChildFilters = (key, data: TFilterRequest) => {
  const tmpFilters = [];
  if (Array.isArray(data?.value)) {
    data.value?.length &&
      tmpFilters.push({
        // [key]: { [data.operator === 'is' ? `$in` : `$notin`]: data.value },
        [key]: {
          [data.operator === 'is'
            ? `$in`
            : data.operator === 'ne'
              ? '$notin'
              : data.operator]: data.value,
        },
      });
    return tmpFilters;
  }

  if (
    typeof data?.value !== 'object' &&
    ['is', 'nis', 'contL'].includes(data?.operator)
  ) {
    tmpFilters.push({
      // [key]: data.operator === 'is' ? data.value : { [`$nlike`]: data.value },
      [key]:
        data?.operator === 'is'
          ? data?.value
          : data?.operator === 'contL'
            ? { [`$contL`]: data?.value }
            : { [`$nlike`]: data?.value },
    });
  } else if (data?.operator) {
    tmpFilters.push({
      [key]: { [`$${data?.operator}`]: `${data?.value}` },
    });
  }

  return tmpFilters;
};

// export const formatFilterInSearch = (rawFilter) => {
//   const filter = [];

//   Object.keys(getValidData(rawFilter)).forEach((key) => {
//     const filters = getFilterInSearch(key, rawFilter[key]);
//     filters.forEach((e) => {
//       (e || e === false) && filter.push(e);
//     });
//   });
//   return filter.length ? filter : undefined;
// };
export const formatFilter = (rawFilter: TFilterRequest[]) => {
  if (!(Array.isArray(rawFilter) && rawFilter?.length > 0)) return undefined;
  const filter = {};

  const format = (data) => {
    const resultArray = data.map((item) => {
      if (Array.isArray(item.value) && typeof item?.value?.[0] === 'object') {
        const subConditionKey = `$${item.value?.[0]?.condition || 'and'}`;
        const subArray = item.value
          .map((subItem) => getChildFilters(subItem?.key, subItem))
          .flat();
        return { [subConditionKey]: subArray };
      } else {
        return getChildFilters(item.key, item)[0];
      }
    });

    return resultArray;
  };

  if (
    rawFilter.findIndex((e) => e?.condition === 'and') !== -1 &&
    rawFilter.findIndex((e) => e?.condition === 'or') !== -1
  ) {
    const resultOr = format(rawFilter.filter((e) => e?.condition === 'or'));
    const resultAnd = format(rawFilter.filter((e) => e?.condition === 'and'));
    filter[`$and`] = [...resultAnd, { $or: resultOr }];
  } else {
    // Extract the condition from the top-level
    const topLevelCondition =
      rawFilter.length > 0
        ? rawFilter.findIndex((e) => e?.condition === 'and') !== -1
          ? 'and'
          : rawFilter[0].condition
        : 'and';

    const result = format(rawFilter);

    filter[`$${topLevelCondition}`] = result;
  }

  return Object.keys(filter).length ? filter : undefined;
};

const formatSorter = (orderBy: string | string[]) => {
  if (!orderBy) return 'createdAt,DESC';
  if (Array.isArray(orderBy)) return orderBy;
  if (orderBy?.[0] === '-')
    return orderBy?.substring(1, orderBy?.length) + ',DESC';
  return orderBy + ',ASC';
};

const formatSQuery = (requestParams, q, s) => {
  if ((requestParams.filters as DynamicObjectType)?.customSearch) {
    return JSON.stringify(
      (requestParams.filters as DynamicObjectType)?.customSearch,
    );
  }
  const arr = compact([requestParams?.rawQuery, q, s]);
  return JSON.stringify(arr?.length > 1 ? { $and: arr } : arr?.[0]);
};

export const convertRequestParams = (type, params: RequestParamsType = {}) => {
  const { isPassNull, ...requestParams } = params;
  // const filterInSearch =
  //   formatFilterInSearch(
  //     omit(getValidData(requestParams.filters, true), 'q') || {},
  //   ) || [];
  // const q = requestParams.q || { $and: filterInSearch };

  // if (requestParams.q) {
  //   filterInSearch.forEach((e) => {
  //     q.$and.push(e);
  //   });
  // }

  const q = formatFilter(
    (requestParams.filters as DynamicObjectType)?.customSearch
      ? requestParams.filters
      : requestParams?.filters?.filter(
          (e) => !!e && (e?.value || e?.value?.toString() === 'false'),
        ) || [],
  );
  const s = formatFilter(
    requestParams?.s?.filter(
      (e) => !!e && (e?.value || e?.value?.toString() === 'false'),
    ) || [],
  );

  const formatedParams: FormattedRequestParamsType = {
    ...omit(requestParams, [
      'q',
      'orderBy',
      'limit',
      'pageSize',
      'offset',
      'count',
      'filter',
      'filters',
      'rawQuery',
      'search',
      'outSideFilter',
    ]),
    ...requestParams?.outSideFilter,
    page:
      Number(requestParams?.page) ||
      Number((requestParams?.offset || 0) / (requestParams?.limit || 10)) + 1,
    limit: requestParams?.limit || 10,
    s: formatSQuery(requestParams, q, s),
    sort: formatSorter(requestParams.orderBy),
    filter: requestParams?.filter,
  };
  switch (type) {
    case 'GET_ALL':
      return formatedParams;
    case 'GET_BY_ID':
      return {
        ...requestParams,
      };
    case 'EDIT':
      delete formatedParams.id;
      delete formatedParams.filter;
      delete formatedParams.q;
      delete formatedParams.count;

      return isPassNull
        ? omit(requestParams, ['limit', 'id', 'skip'])
        : getValidData(omit(requestParams, ['limit', 'id', 'skip']));
    case 'CREATE':
      return isPassNull
        ? omit(requestParams, ['limit', 'skip'])
        : getValidData(omit(requestParams, ['limit', 'skip']));
    case 'DELETE':
    default:
      return {};
  }
};

export const convertResponseData = (
  type,
  response,
  options = { primaryKey: PRIMARY_KEY },
) => {
  switch (type) {
    case 'GET_ALL':
      return {
        data: keyBy(
          response?.data.map((data) => ({
            ...data,
            id: data[options.primaryKey || PRIMARY_KEY],
            backupId: data[PRIMARY_KEY],
          })),
          options.primaryKey || PRIMARY_KEY,
        ),
        ids: response?.data.map(
          (data) => data[options.primaryKey || PRIMARY_KEY],
        ),
        total: response?.total,
      };
    case 'GET_BY_ID':
    case 'CREATE':
      return response
        ? {
            ...response,
            id: response[options.primaryKey || PRIMARY_KEY],
            backupId: response[PRIMARY_KEY],
          }
        : null;
    case 'EDIT':
      return response && response
        ? {
            ...response,
            id: response[options.primaryKey || PRIMARY_KEY],
            backupId: response[PRIMARY_KEY],
          }
        : null;
    case 'DELETE':
    default:
      return response;
  }
};
